import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { merge } from "lodash";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { LS_EXPIRE_HOURS } from "../config/ls-expire-time";
import type { BloodTestPackage } from "../models";
import type { Agreements, PersonalDetails, PhoneInfo, VerificationType } from "../types";

export type OrderingStore = {
  bloodTestPackage?: BloodTestPackage;
  timeSlot?: Date;
  personalDetails?: PersonalDetails;
  agreements?: Agreements;
  verificationType?: VerificationType;
  verificationTokenUuid?: string;
  expireTime?: string;
  selectBloodTestPackage: (bloodTestPackage: BloodTestPackage) => void;
  selectTimeSlot: (timeSlot?: Date) => void;
  setDateOfBirth: (dateOfBirth: PersonalDetails["dateOfBirth"]) => void;
  setPesel: (pesel: PersonalDetails["pesel"]) => void;
  setPhoneInfo: (phoneInfo: PhoneInfo) => void;
  setAgreements: (agreements: Agreements) => void;
  setEmail: (email: PersonalDetails["email"]) => void;
  setFullName: (firstName: PersonalDetails["firstName"], lastName: PersonalDetails["lastName"]) => void;
  setVerificationType: (verificationType: VerificationType) => void;
  setVerificationTokenUuid: (verificationTokenUuid: string | undefined) => void;
  reset: () => void;
};

type DefaultState = Pick<OrderingStore, "expireTime">;

const defaultState: DefaultState = {
  expireTime: dayjs().add(LS_EXPIRE_HOURS, "hour").toISOString(),
};

export const useOrderingStore = create<OrderingStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultState,
        selectBloodTestPackage: (bloodTestPackage) =>
          set(() => ({
            bloodTestPackage,
          })),
        selectTimeSlot: (timeSlot) =>
          set(() => ({
            timeSlot,
          })),
        setDateOfBirth: (dateOfBirth) =>
          set((store) => ({
            personalDetails: {
              ...store.personalDetails,
              dateOfBirth,
            } as PersonalDetails,
          })),
        setPesel: (pesel) =>
          set((store) => ({
            personalDetails: {
              ...store.personalDetails,
              pesel,
            } as PersonalDetails,
          })),
        setAgreements: (agreements) =>
          set(() => ({
            agreements,
          })),
        setPhoneInfo: (phoneInfo) =>
          set((store) => ({
            personalDetails: {
              ...store.personalDetails,
              ...phoneInfo,
            } as PersonalDetails,
          })),
        setFullName: (firstName, lastName) =>
          set((store) => ({
            personalDetails: {
              ...store.personalDetails,
              firstName,
              lastName,
            } as PersonalDetails,
          })),
        setEmail: (email) =>
          set((store) => ({
            personalDetails: {
              ...store.personalDetails,
              email,
            } as PersonalDetails,
          })),
        setVerificationType: (verificationType) =>
          set(() => ({
            verificationType,
          })),
        setVerificationTokenUuid: (verificationTokenUuid) =>
          set(() => ({
            verificationTokenUuid,
          })),
        reset: () => {
          set(() => ({
            bloodTestPackage: undefined,
            timeSlot: undefined,
            personalDetails: undefined,
            agreements: undefined,
            verificationType: undefined,
            verificationTokenUuid: undefined,
          }));

          useOrderingStore.persist.clearStorage();
        },
      }),
      {
        name: "OrderingGroupActionsStore",
        merge: (pState, cState) => {
          if (!pState) return cState;
          const persistedState = pState as OrderingStore;

          persistedState.timeSlot = persistedState.timeSlot ? dayjs(persistedState.timeSlot).toDate() : undefined;

          return merge(cState, persistedState);
        },
      },
    ),
  ),
);
